import { ModuleKey } from '@masamune/shared';

/**
 * FASE 0 — Congelamento dei moduli esistenti (solo UI, reversibile).
 *
 * Nel DB e nell'API non esiste un concetto di modulo attivo/disattivo: la
 * fonte di verità è QUESTA costante. Un modulo "in pausa" resta nel codice,
 * nel DB e nei permessi; cambia solo quello che la UI mostra:
 *  - Sidebar: voce visibile ma smorzata, con badge "In pausa" (Sidebar.tsx)
 *  - Route: <FrozenGate> mostra la pagina di pausa al posto della reale
 *    (App.tsx), quindi vale per link interni, palette, stream Oggi e URL a mano
 *  - "+ Nuovo" e palette: nessuna creazione né "Vai a…" verso un modulo in pausa
 *    (newActions.ts, CommandPalette.tsx)
 *  - Topbar: breadcrumb "… / In pausa"
 * Per riattivare un modulo basta toglierlo da FROZEN_MODULES.
 */
/**
 * NOTA — FATTURAZIONE non è qui, ed è voluto. La Contabilità è confluita in
 * Amministrazione (tab "Documenti"): è sparita la voce di Sidebar, non il
 * modulo. Congelarlo spegnerebbe anche i dettagli /invoices/:id e /quotes/:id
 * (il gate lavora per prefisso di path) e toglierebbe "Fattura"/"Preventivo"
 * dal menu "+ Nuovo", che invece devono restare.
 */
/**
 * 19/09/2026 — TUTTO RIAPERTO, su richiesta di Lorenzo: vuole girare per
 * l'applicazione e decidere lui, guardandola, cosa tenere e cosa spegnere.
 * Il congelamento di fase 0 era una scelta sua di due giorni prima, quindi
 * questa la sostituisce.
 *
 * L'elenco resta vuoto, non cancellato: per rimettere in pausa un modulo basta
 * aggiungerlo qui e l'infrastruttura del gate è ancora tutta al suo posto.
 *
 * ⚠️ Le sezioni riaperte sono quelle costruite da Samuele e mai riviste coi
 * dati veri: possono essere vuote o mostrare cose che non tornano. È atteso,
 * non è un guasto.
 */
export const FROZEN_MODULES: ModuleKey[] = [];

export function isFrozen(module: ModuleKey): boolean {
  return FROZEN_MODULES.includes(module);
}

/**
 * Route → modulo (prefisso di pathname: combacia il path esatto o path + "/").
 * Le pagine di dettaglio seguono il modulo della lista che le apre.
 * `/projects/:id` appartiene alla divisione del progetto (MKT/PROD/DESIGN) ma
 * la route non la conosce senza una fetch: si usa PRODUCT come rappresentante,
 * perché le tre divisioni si congelano insieme. Se un giorno se ne riattiva
 * una sola, qui andrà letta la divisione del progetto.
 * Per ogni modulo la PRIMA riga è la sua pagina "home" (vedi moduleHome).
 */
const ROUTE_MODULES: Array<{ prefix: string; module: ModuleKey }> = [
  { prefix: '/tasks',               module: ModuleKey.TASKS },
  { prefix: '/trattative',          module: ModuleKey.TRATTATIVE },
  { prefix: '/crm',                 module: ModuleKey.CRM },
  { prefix: '/clients',             module: ModuleKey.CRM },
  // Progetti: il permesso e' CRM, lo stesso che l'API chiede per crearne uno
  { prefix: '/progetti',            module: ModuleKey.CRM },
  { prefix: '/divisions/marketing', module: ModuleKey.MARKETING },
  { prefix: '/divisions/product',   module: ModuleKey.PRODUCT },
  { prefix: '/divisions/design',    module: ModuleKey.DESIGN },
  { prefix: '/projects',            module: ModuleKey.PRODUCT },
  { prefix: '/talent',              module: ModuleKey.TALENT },
  { prefix: '/courses',             module: ModuleKey.TALENT },
  { prefix: '/candidates',          module: ModuleKey.TALENT },
  { prefix: '/suppliers',           module: ModuleKey.SUPPLIERS },
  { prefix: '/services',            module: ModuleKey.SERVICES },
  { prefix: '/vault',               module: ModuleKey.VAULT },
  { prefix: '/report',              module: ModuleKey.REPORT },
  { prefix: '/invoices',            module: ModuleKey.FATTURAZIONE },
  { prefix: '/quotes',              module: ModuleKey.FATTURAZIONE },
  { prefix: '/admin',               module: ModuleKey.AMMINISTRAZIONE },
  { prefix: '/settings',            module: ModuleKey.SETTINGS },
];

/** Modulo a cui appartiene un path, null se non è una route di modulo. */
export function moduleForPath(pathname: string): ModuleKey | null {
  if (pathname === '/') return ModuleKey.DASHBOARD;
  const hit = ROUTE_MODULES.find((r) => pathname === r.prefix || pathname.startsWith(r.prefix + '/'));
  return hit?.module ?? null;
}

/** Modulo in pausa a cui appartiene il path, null se la pagina è viva. */
export function frozenModuleForPath(pathname: string): ModuleKey | null {
  const m = moduleForPath(pathname);
  return m && isFrozen(m) ? m : null;
}

/** Pagina "home" di un modulo (la voce di Sidebar), usata dal breadcrumb. */
export function moduleHome(module: ModuleKey): string {
  if (module === ModuleKey.DASHBOARD) return '/';
  return ROUTE_MODULES.find((r) => r.module === module)?.prefix ?? '/';
}
